import fetch from 'unfetch';
import { merge } from 'lodash';
import { trimCharsStart } from 'lodash/fp';
import { APIConfig, ClientRequest, Middleware } from './types';

/**
  * When I originally created this client, I was focused on
  * immutablity but I now realize that this client is way over
  * engineered for what it needs to be. Every function aside from
  * get and post returns an entirely new API object.
  */
export class API {
  public client: ClientRequest = ({
    path,
    data,
    method,
    options,
  }) => (
    fetch(
      path,
      {
        method,
        body: data,
        ...options,
      },
    )
  );

  public path = '';

  public base = '';

  public options: Partial<RequestInit> = {};

  private middleware: Middleware[] = [];

  constructor(config?: Partial<APIConfig>) {
    this.client = config?.client ?? this.client;
    this.base = config?.base ?? this.base;
    this.path = config?.path ?? this.path;
    this.middleware = config?.middleware ?? this.middleware;
    this.options = config?.options ?? this.options;

    this._request.bind(this);
  }

  public appendPath(path: string): API {
    return new API({
      client: this.client,
      base: this.base,
      path: this.path.concat(path),
      options: this.options,
      middleware: this.middleware,
    });
  }

  public withMiddleware(middleWare: Middleware | Middleware[]): API {
    return new API({
      client: this.client,
      base: this.base,
      path: this.path,
      options: this.options,
      middleware: [this.middleware, middleWare].flat(),
    });
  }

  public withOptions(options: RequestInit): API {
    return new API({
      client: this.client,
      base: this.base,
      path: this.path,
      options: merge(this.options, options),
      middleware: this.middleware,
    });
  }

  public withBase(base: string): API {
    return new API({
      client: this.client,
      base,
      path: this.path,
      options: this.options,
      middleware: this.middleware,
    });
  }

  public withClient(client: ClientRequest): API {
    return new API({
      client,
      base: this.base,
      path: this.path,
      options: this.options,
      middleware: this.middleware,
    });
  }

  public withHeaders(headers: Record<string, string>): API {
    return this.withOptions({
      headers: merge(this.options?.headers || {}, headers),
    });
  }

  public withCredentials(): API {
    return this.withOptions({
      credentials: 'include',
    });
  }

  /**
    * Recursively creates a middleware pipeline. The first middleware in the
    * array will be the last middleware to be called
    */
  private _createPipeline([currentMiddleware, ...rest]: Middleware[]): ClientRequest {
    if (!currentMiddleware) {
      return this.client;
    }

    return currentMiddleware(this._createPipeline(rest));
  }

  private _request<T, R = unknown>(method: RequestInit['method'], data?: T) {
    if (this.path.length === 0 && process.env.NODE_ENV == 'development') {
      console.warn(`Invoking client for ${this.base} without a specified path`);
    }

    const fullPath = `${this.base}/${trimCharsStart('/')(this.path)}`;

    // This PROBABLY should be initialized after middleware is added so that
    // this doesn't have to run every single time a request is made but for now
    // this should do the trick
    const requestPipeline = this._createPipeline(this.middleware);

    return requestPipeline({
      path: fullPath,
      method,
      options: this.options,
      data,
    });
  }

  public async get<T = unknown>(data?: T) {
    return this._request('GET', data);
  }

  public async post<T = unknown>(data?: T) {
    return this._request('POST', data);
  }
}
